import { useState, useEffect, useCallback } from 'react';
import { 
  Typography, 
  Box, 
  Button, 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Stack,
  Avatar
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface LeaderboardEntry {
  memberId: string;
  name: string;
  score: number;
  lastUpdated: Date;
  rank?: number;
}

interface Member {
  name: string;
  icon?: string;
}

interface Members {
  [key: string]: Member | null;
}

interface ScoreService {
  getLeaderboard: (maxEntries: number) => Promise<LeaderboardEntry[]>;
}

interface LeaderboardProps {
  scoreService: ScoreService;
  members: Members;
}

/**
 * Leaderboard Component
 * 
 * Displays the account's private leaderboard showing only family members using Material-UI
 */
const Leaderboard: React.FC<LeaderboardProps> = ({ scoreService, members }) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to get emoji icon
  const getEmojiIcon = (icon?: string) => {
    return icon || '😊';
  };

  // Define fetchLeaderboard with useCallback to prevent it from changing on every render
  const fetchLeaderboard = useCallback(async () => {
    try {
      setLoading(true);
      const data = await scoreService.getLeaderboard(10);
      setLeaderboard(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [scoreService]);

  useEffect(() => {
    // Fetch leaderboard when component mounts
    if (scoreService) {
      fetchLeaderboard();
    } else {
      setLoading(false);
      setError('No score service available');
    }
  }, [scoreService, fetchLeaderboard]);

  // If there's an error, display it
  if (error) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4" gutterBottom>
          Family Leaderboard
        </Typography>
        <Typography color="error">
          {error}
        </Typography>
      </Stack>
    );
  }

  // If loading, show a loading message
  if (loading) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4" gutterBottom>
          Family Leaderboard
        </Typography>
        <Box display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      </Stack>
    );
  }

  // If no scores, show a message
  if (leaderboard.length === 0) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4" gutterBottom>
          Leaderboard
        </Typography>
        <Typography>
          No family members have scores yet. Add some family members and start tracking scores!
        </Typography>
      </Stack>
    );
  }

  // Render the leaderboard
  return (
    <Stack spacing={2}>
      <Typography variant="h4" gutterBottom>
        Leaderboard
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="15%" align="center"><strong>Rank</strong></TableCell>
              <TableCell><strong>Member</strong></TableCell>
              <TableCell width="25%" align="right"><strong>Score</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboard.map((entry) => (
              <TableRow key={entry.memberId}>
                <TableCell align="center">
                  <strong>{entry.rank}</strong>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', fontSize: '1.2rem', width: 32, height: 32 }}>
                      {getEmojiIcon(members[entry.memberId]?.icon)}
                    </Avatar>
                    <Typography variant="body1">{entry.name}</Typography>
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <strong>{entry.score}</strong>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box textAlign="center">
        <Button 
          variant="contained" 
          color="primary" 
          onClick={fetchLeaderboard}
          startIcon={<RefreshIcon />}
        >
          Refresh
        </Button>
      </Box>
    </Stack>
  );
};

export default Leaderboard;